import { useFormik } from "formik";
import { useMemo } from "react";
import { Card, CardBody, Col, Container, Form, Row } from "reactstrap";
import HeaderBox from "../header-box/index";
import validationSchema from "./validationSchema";
import Input from "../../../../components/Common/Input";
import Select from "../../../../components/Common/Select";
import { STATES_AND_COUNTRIES } from "../../../../common/constants";
import { MachineApplicationOptions } from "../../../../constants/common";
import { DealerSetupWarning } from "../../../../components/Common";

interface Props {
    data?: any; // TODO: Define appropriate type
    user?: any; // TODO: Define appropriate type
    selectedOwner?: any; // TODO: Define appropriate type - used when admin registers on behalf of owner
    deviceSerialNumber: string;
    onNext: (_data?: any) => void;
    onPrev: (_data?: any) => void;
}

const UserInformationForm = ({ data = null, user = null, selectedOwner = null, onNext, onPrev, deviceSerialNumber }: Props) => {
    // Use selectedOwner if provided (admin flow), otherwise use logged-in user
    const ownerData = selectedOwner || user;

    const form = useFormik({
        initialValues: {
            firstName: data?.firstName || ownerData?.firstName || "",
            lastName: data?.lastName || ownerData?.lastName || "",
            email: data?.email || ownerData?.email || "",
            phoneNumber: data?.phoneNumber || ownerData?.phone || "",
            company: data?.company || ownerData?.company?.name || "",
            machineApplication: data?.machineApplication  || ownerData?.company?.machineApplication || "",
            address: data?.address || ownerData?.address?.line1 || "",
            address2: data?.address2 || ownerData?.address?.line2 || "",
            city: data?.city || ownerData?.address?.city || "",
            state: data?.state || ownerData?.address?.state || "",
            zip: data?.zip || ownerData?.address?.zip || "",
            country: "",
        },
        validationSchema,
        validateOnMount: true,
        onSubmit: () => {
            onNext({
                user: form.values,
            });
        },
    });

    const isCompanyDisabled = useMemo(() => !!ownerData?.company?.id, [ownerData]);
    const isAddressDisabled = useMemo(() => !!ownerData?.address?.id, [ownerData]);
    const isButtonDisabled = useMemo(() =>
            !!Object.keys(form.errors).length
        , [form]);

    const states = useMemo(() => STATES_AND_COUNTRIES.map(({ State, StateCode }) => ({ label: State, value: StateCode })), [STATES_AND_COUNTRIES]);

    return (
        <>
            <HeaderBox
                title="User details"
                serialNumber={deviceSerialNumber}
                onPrev={onPrev}
            />

            <Card>
                <CardBody>
                    <Container fluid>
                        <Form
                            onSubmit={e => {
                                e.preventDefault();
                                form.handleSubmit();
                                return false;
                            }}
                        >
                            <Row className="mb-4">
                                <Col xs={12} className="font-size-18 fw-bold pb-3 text-uppercase">
                                    User Information
                                </Col>
                                <Col xs={12} xl={6}>
                                    <div className="mb-3">
                                        <Input
                                            label="First Name"
                                            name="firstName"
                                            placeholder="First Name"
                                            form={form}
                                            value={form.values.firstName}
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} xl={6}>
                                    <div className="mb-3">
                                        <Input
                                            label="Last Name"
                                            name="lastName"
                                            placeholder="Last Name"
                                            form={form}
                                            value={form.values.lastName}
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} xl={6}>
                                    <div className="mb-3">
                                        <Input
                                            label="Email Address"
                                            name="email"
                                            placeholder="Email Address"
                                            form={form}
                                            value={form.values.email}
                                            disabled
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} xl={6}>
                                    <div className="mb-3">
                                        <Input
                                            label="Phone Number"
                                            name="phoneNumber"
                                            placeholder="Phone Number"
                                            form={form}
                                            value={form.values.phoneNumber}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col xs={12} className="font-size-18 fw-bold pb-3 text-uppercase">
                                    Company Information {ownerData?.company?.id}
                                </Col>
                                <Col xs={12} xl={6}>
                                    <div className="mb-3">
                                        <Input
                                            label="Company"
                                            name="company"
                                            placeholder="Company"
                                            form={form}
                                            value={form.values.company}
                                            disabled={isCompanyDisabled}
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} xl={6}>
                                    <div className="mb-3">
                                        <Select
                                            label="Machine Application"
                                            name="machineApplication"
                                            placeholder="Machine Application"
                                            options={MachineApplicationOptions}
                                            formik={form}
                                            value = {
                                                MachineApplicationOptions.filter(option =>
                                                    option.value === form.values.machineApplication)
                                            }
                                            
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12} className="font-size-18 fw-bold pb-3 text-uppercase">
                                    Address
                                </Col>
                                <Col xs={12} xl={6}>
                                    <div className="mb-3">
                                        <Input
                                            label="Address"
                                            name="address"
                                            placeholder="Address"
                                            form={form}
                                            value={form.values.address}
                                            disabled={isAddressDisabled}
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} xl={6}>
                                    <div className="mb-3">
                                        <Input
                                            label="Address 2"
                                            name="address2"
                                            placeholder={isAddressDisabled ? "" : "Address 2"}
                                            form={form}
                                            value={form.values.address2}
                                            disabled={isAddressDisabled}
                                        />
                                    </div>
                                </Col>
                                <Col xs={6} xl={3}>
                                    <div className="mb-3">
                                        <Input
                                            label="City"
                                            name="city"
                                            placeholder="City"
                                            form={form}
                                            value={form.values.city}
                                            disabled={isAddressDisabled}
                                        />
                                    </div>
                                </Col>
                                <Col xs={6} xl={3}>
                                    <div className="mb-3">
                                        <Select
                                            name="state"
                                            label="State"
                                            placeholder="Select State"
                                            options={states}
                                            formik={form}
                                            value = {
                                                states.filter(option =>
                                                    option.value === form.values.state
                                                )
                                            }
                                            isDisabled={isAddressDisabled}
                                        ></Select>
                                    </div>
                                </Col>
                                <Col xs={6} xl={3}>
                                    <div className="mb-3">
                                        <Input
                                            label="Zipcode"
                                            name="zip"
                                            placeholder="Zipcode"
                                            form={form}
                                            value={form.values.zip}
                                            disabled={isAddressDisabled}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <div className="text-center mt-3">
                                        <button
                                            type="submit"
                                            className="btn btn-primary save-user"
                                            disabled={isButtonDisabled}
                                        >
                                            SAVE & CONTINUE
                                        </button>
                                        <DealerSetupWarning />
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </Container>
                </CardBody>
            </Card>
        </>
    );
};

export default UserInformationForm;
